import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import axiosInstance from '../../../utils/axios/axios';
import {
  offerwallApi,
  useGetAdsQuery,
} from '../../../services/gmoOfferwallApi';
import { Layout } from '../../components/Layout';
import {
  createOfferwallCsvContent,
  OFFERWALL_CSV_HEADER_LABELS,
  OFFERWALL_CSV_HEADERS,
} from '../OfferwallPage/form';

type OfferwallImportResponse = {
  inserted: number;
  skipped: number;
};

export function OfferwallCsvPage() {
  const dispatch = useDispatch();
  const { data = [], isLoading } = useGetAdsQuery();
  const [file, setFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const exampleHeaders = useMemo(
    () =>
      OFFERWALL_CSV_HEADERS.map(
        header => OFFERWALL_CSV_HEADER_LABELS[header],
      ).join(', '),
    [],
  );

  const handleExport = () => {
    const csvContent = createOfferwallCsvContent(data);
    const blob = new Blob(['\uFEFF' + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `offerwall_export_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('CSVファイルを選択してください。', {
        position: 'bottom-left',
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);

    try {
      setIsImporting(true);
      const response = await axiosInstance.post<OfferwallImportResponse>(
        '/offerwall_import_csv',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      toast.success(
        `${response.inserted}件を取り込みました。空行スキップ: ${response.skipped}件`,
        {
          position: 'bottom-left',
        },
      );
      dispatch(offerwallApi.util.invalidateTags(['offerwall']));
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(
        error?.message || 'CSVの取り込みに失敗しました。ファイル内容を確認してください。',
        {
          position: 'bottom-left',
          autoClose: 8000,
        },
      );
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Layout title={'案件CSV管理'}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h5">案件CSV管理</Typography>
            <Typography variant="body2" color="text.secondary">
              エクスポートしたCSVをそのまま編集して、複数行まとめて取り込めます。
            </Typography>
            <Alert severity="info">
              CSVヘッダは固定です: {exampleHeaders}
            </Alert>
            <Box>
              <Button
                variant="contained"
                onClick={handleExport}
                disabled={isLoading || isImporting}
              >
                現在の案件をCSV出力
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">CSVインポート</Typography>
            <Typography variant="body2" color="text.secondary">
              1行につき1件追加されます。既存データの更新ではなく、新規追加です。
            </Typography>
            <Alert severity="warning">
              CSVの1行目には、上記のヘッダを必ず入力してください。ヘッダがない、または列の順番が異なるCSVは取り込めません。
            </Alert>
            <Alert severity="info">
              <Typography variant="body2">
                表示: 表示する案件は <strong>true</strong>、非表示にする案件は <strong>false</strong> を入力してください。
              </Typography>
              <Typography variant="body2">
                順位: ボーナス案件の表示順に使用します。数値が大きい案件ほど先に表示されます。
              </Typography>
              <Typography variant="body2">
                ボーナスタイプ: ボーナス案件として扱う場合は <strong>true</strong>、通常案件は <strong>false</strong> を入力してください。
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                ユーザーが申し込んだボーナス案件の案件表示名と、ポイント付与時のタイトルが一致すると、ボーナスポイントが自動で付与されます。管理画面からのポイント付与、管理画面CSVアップロードによるポイント付与のどちらにも適用されます。
              </Typography>
            </Alert>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={event => {
                const selectedFile = event.target.files?.[0] || null;
                setFile(selectedFile);
              }}
            />
            <Typography variant="body2">
              選択中: {file?.name || '未選択'}
            </Typography>
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleImport}
                disabled={!file || isImporting}
                startIcon={isImporting ? <CircularProgress size={18} /> : null}
              >
                {isImporting ? '取り込み中...' : 'CSVを取り込む'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </Layout>
  );
}

export default OfferwallCsvPage;
